import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const UIContext = createContext(null);

export const useUI = () => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
};

export const UIProvider = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The rows the call was opened from, so the drawer can step through them.
  // Empty when the opener has no list (a permalink, a single follow-up).
  const [callQueue, setCallQueue] = useState([]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  /**
   * Open a call. `queue` is the list it sits in, in display order; pass it and
   * the drawer's previous/next buttons and j/k keys walk that list.
   */
  const handleShowTranscript = useCallback((call, queue) => {
    setSelectedCall(call);
    setCallQueue(Array.isArray(queue) ? queue : []);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCall(null);
    setCallQueue([]);
  }, []);

  /**
   * Where the open call sits in its queue. By identity first — callId is not
   * unique in production, so two rows can share one — then by id for a call
   * object that was rebuilt after it was queued.
   */
  const queueIndex = useMemo(() => {
    if (!selectedCall || callQueue.length === 0) return -1;
    const byRef = callQueue.indexOf(selectedCall);
    if (byRef !== -1) return byRef;
    const id = selectedCall.callId || selectedCall.id;
    return id ? callQueue.findIndex((c) => (c.callId || c.id) === id) : -1;
  }, [callQueue, selectedCall]);

  const showPrevCall = useCallback(() => {
    if (queueIndex > 0) setSelectedCall(callQueue[queueIndex - 1]);
  }, [callQueue, queueIndex]);

  const showNextCall = useCallback(() => {
    if (queueIndex !== -1 && queueIndex < callQueue.length - 1) {
      setSelectedCall(callQueue[queueIndex + 1]);
    }
  }, [callQueue, queueIndex]);

  const queueNav =
    queueIndex === -1
      ? null
      : {
          onPrev: showPrevCall,
          onNext: showNextCall,
          hasPrev: queueIndex > 0,
          hasNext: queueIndex < callQueue.length - 1,
          position: { index: queueIndex + 1, total: callQueue.length },
        };

  return (
    <UIContext.Provider
      value={{
        isSidebarOpen,
        selectedCall,
        isModalOpen,
        queueNav,
        toggleSidebar,
        handleShowTranscript,
        handleCloseModal,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};
